import { DomainForm } from "@/components/domain";
import { FormErrors } from "@/components/field";
import { PropertyList } from "@/components/property-list";
import { SelectSearch } from "@/components/select-search";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  useGetClient,
  useGetClients,
  useCreateClient,
  useUpdateClient,
  getClientQueryOptions,
  getClientsQueryOptions,
} from "@/hooks/clients";
import { T } from "@/i18n";
import { cn, dateFormatedToString, formatPhoneNumber } from "@/lib/utils";
import { Client, ReservationClient } from "@/models";
import { useForm } from "@deadcow-enterprises/react-forms";
import { Flag, Pencil, Plus, Trash } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { z } from "zod";
import { useReservationFormContext } from "./contex";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { commandItemStyle } from "@/components/ui/command";
import { useQueryClient } from "@tanstack/react-query";

export const clientKeysToList: Array<keyof (Client & ReservationClient)> = [
  "phone",
  "birthdate",
  "country",
  "language",
  "height",
  "allergies",
  "dietaryRestrictions",
  "emergencyContact",
  "ensured",
  "inseam",
  "medicalConditions",
  "passportExpiration",
  "passportNumber",
  "needsBike",
  "arrivalDetails",
  "departureDetails",
];

export function ClientsTab() {
  const {
    reservationClients,
    addReservationClient,
    setGroupLeader,
    groupLeaderId,
    isDirty,
    setReservationClientValidator,
  } = useReservationFormContext();

  const form = useForm<{ groupLeaderId: number }>({
    groupLeaderId: {
      value: groupLeaderId,
      validator: z.number().min(0, "errors.groupLeaderRequired"),
    },
  });

  useEffect(() => {
    setReservationClientValidator(() => form.updateValidity());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDirty && !form.dirty) {
      form.updateValidity();
      form.markAsDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.dirty, isDirty]);

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-4">
      <div className="flex justify-end">
        <UpsertReservationClient
          onUpsert={async (rc) => {
            addReservationClient(rc);
            form.markAsDirty();
          }}
        >
          <Button type="button">
            <Plus />
            <T tKey={`reservations.form.clients.new`} />
          </Button>
        </UpsertReservationClient>
      </div>

      <FormErrors errors={form.errors.groupLeaderId} />
      <div className="grid gap-2">
        {reservationClients.map((rc) => (
          <ClientCard
            markFormAsDirty={() => form.markAsDirty()}
            client={rc}
            key={rc.clientId}
            isGroupLeader={groupLeaderId === rc.clientId}
            onSetGroupLeader={(v) => {
              form.setValue("groupLeaderId", v);
              setGroupLeader(v.toString());
            }}
          />
        ))}
      </div>
    </div>
  );
}

function ClientCard({
  onSetGroupLeader,
  isGroupLeader,
  client,
  markFormAsDirty,
}: {
  client: ReservationClient;
  isGroupLeader: boolean;
  onSetGroupLeader: (id: number) => void;
  markFormAsDirty: () => void;
}) {
  const { data: details, isLoading } = useGetClient(client.clientId.toString());
  const { updateReservationClient, removeReservationClient } =
    useReservationFormContext();
  return (
    <Card className={cn("relative", isGroupLeader && "border-yellow-500")}>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          {isLoading || !details ? (
            <Skeleton className="h-6 w-1/4" />
          ) : (
            <CardTitle> {details.name}</CardTitle>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => {
                onSetGroupLeader(isGroupLeader ? -1 : client.clientId);
              }}
            >
              <Flag className={cn(isGroupLeader && "fill-yellow-500")} />
            </Button>
            <UpsertReservationClient
              onUpsert={async (rc) => {
                markFormAsDirty();
                await updateReservationClient(client.clientId, rc);
              }}
              model={client}
            >
              <Button type="button" variant="ghost" size="icon">
                <Pencil />
              </Button>
            </UpsertReservationClient>

            <Button
              type="button"
              variant="ghost-destructive"
              size="icon"
              onClick={() => {
                removeReservationClient(client.clientId.toString());
              }}
            >
              <Trash />
            </Button>
          </div>
        </div>
        <CardDescription>
          {isLoading || !details ? (
            <Skeleton className="h-6 w-1/4" />
          ) : (
            details?.email
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-1">
        <PropertyList
          loading={isLoading}
          item={{ ...(details ?? {}), ...client }}
          labelPrefix="reservations.form.clients.fields"
          properties={clientKeysToList}
          propertyMappingOverrides={{
            birthdate: (v) => (!v ? "-" : dateFormatedToString(new Date(v))),
            phone: (v) => (v ? formatPhoneNumber(v) : "-"),
          }}
        />
      </CardContent>
      {isGroupLeader && (
        <Badge
          className={cn(
            "absolute left-4 top-0 -translate-y-1/2 bg-yellow-500 font-bold text-black",
          )}
        >
          <T tKey="reservations.form.clients.leader" />
        </Badge>
      )}
    </Card>
  );
}

function UpsertReservationClient({
  onUpsert,
  children,
  model,
}: {
  children?: ReactNode;
  model?: ReservationClient;
  onUpsert: (v: ReservationClient) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80" align="end" sideOffset={8}>
        <Form
          model={model}
          onUpsert={async (v) => {
            await onUpsert(v);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function Form({
  onUpsert,
  model,
}: {
  model?: ReservationClient;
  onUpsert: (v: ReservationClient) => Promise<void>;
}) {
  const { data: clients, isLoading: clientsLoading } = useGetClients();
  const queryClient = useQueryClient();
  const { mutateAsync: mutateCreateClient } = useCreateClient({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getClientsQueryOptions.queryKey,
      });
    },
  });
  const { mutateAsync: mutateUpdateClient } = useUpdateClient({
    onSuccess: (client) => {
      queryClient.invalidateQueries({
        queryKey: getClientQueryOptions(client.id).queryKey,
      });
    },
  });
  const f = useForm<ReservationClient & { dummyId: string }>({
    id: {
      value: model?.id,
    },
    clientId: {
      value: model?.clientId ?? -1,
      validator: z.number().min(0, "errors.clientRequired"),
    },
    reservationId: {
      value: model?.reservationId ?? -1,
    },
    flightNumber: {
      value: model?.flightNumber,
    },
    needsBike: {
      value: model?.needsBike ?? false,
    },
    pickupAddress: {
      value: model?.pickupAddress,
    },
    arrivalDetails: {
      value: model?.arrivalDetails,
    },
    departureDetails: {
      value: model?.departureDetails,
    },
    dummyId: {
      value: "",
    },
  });
  return (
    <DomainForm
      form={f}
      onUpsert={onUpsert}
      buttonTranslation={
        !model
          ? "reservations.form.clients.createButton"
          : "reservations.form.clients.updateButton"
      }
      fields={[
        {
          key: "clientId",
          label: "reservations.form.clients.fields.clientId",
          input: (form, id) =>
            clientsLoading || !clients ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                className="w-full"
                value={form.value.clientId}
                // createNode={(search) => (
                //   <CreatePerson
                //     name={search}
                //     onUpsert={(c) => {
                //       setClients([c, ...clients]);
                //       form.setValue("clientId", Number.parseInt(c.id));
                //     }}
                //   />
                // )}
                createNode={
                  <UpsertClient
                    onUpsert={async (v) => {
                      await mutateCreateClient(v);
                      //new Promise((resolve) => resolve(console.log(v)));
                    }}
                  />
                }
                onChange={(v) => {
                  form.setValue("clientId", v);
                }}
                options={clients.map((c) => ({
                  value: Number.parseInt(c.id),
                  label: c.name,
                }))}
              />
            ),
        },
        {
          key: "needsBike",
          label: "reservations.form.clients.fields.needsBike",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              className="ml-auto"
              id={id}
              onCheckedChange={(v) => {
                form.setValue("needsBike", v);
              }}
              checked={!!form.value.needsBike}
            />
          ),
        },
        {
          key: "arrivalDetails",
          label: "reservations.form.clients.fields.arrivalDetails",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.arrivalDetails ?? ""}
              onChange={(e) => {
                form.setValue("arrivalDetails", e.target.value);
              }}
            />
          ),
        },
        {
          key: "departureDetails",
          label: "reservations.form.clients.fields.departureDetails",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.departureDetails ?? ""}
              onChange={(e) => {
                form.setValue("departureDetails", e.target.value);
              }}
            />
          ),
        },
        {
          key: "dummyId",
          label: undefined,
          input: (form, id) =>
            form.value.clientId === -1 ? (
              <></>
            ) : (
              <Dialog>
                <DialogTrigger id={id} asChild disabled={!form.value.clientId}>
                  <button
                    className={cn(
                      commandItemStyle,
                      "mt-2 w-full border-spacing-2 cursor-pointer border-t font-bold",
                    )}
                    type="button"
                    disabled={!form.value.clientId}
                  >
                    <Pencil className="mr-2 aspect-square w-4 shrink-0" />
                    <T tKey={"reservations.form.clients.editPersonalInfo"} />
                  </button>
                </DialogTrigger>
                <DialogContent
                  closeId="select-modal-btn"
                  className="max-h-[60dvh] max-w-[90dvw] overflow-y-auto md:max-w-[620px]"
                >
                  <UpsertClient
                    model={clients?.find(
                      (c) => c.id === form.value.clientId.toString(),
                    )}
                    onUpsert={async (v) => {
                      await mutateUpdateClient(v);
                    }}
                  />
                </DialogContent>
              </Dialog>
            ),
        },
      ]}
    />
  );
}

function UpsertClient({
  onUpsert,
  model,
}: {
  onUpsert: (v: Client) => Promise<void>;
  model?: Client;
}) {
  const form = useForm<Client>({
    email: {
      value: model?.email ?? "",
      validator: z.string().email(),
    },
    height: {
      value: model?.height ?? null,
      validator: z.number().nullish(),
    },
    inseam: {
      value: model?.inseam ?? null,
      validator: z.number().nullish(),
    },
    emergencyContact: {
      value: model?.emergencyContact ?? "",
      validator: z.string().nullish(),
    },
    passportExpiration: {
      value: model?.passportExpiration ?? "",
      validator: z.string().nullish(),
    },
    passportNumber: {
      value: model?.passportNumber ?? "",
      validator: z.string().nullish(),
    },
    ensured: {
      value: model?.ensured ?? null,
      validator: z.boolean().nullish(),
    },
    dietaryRestrictions: {
      value: model?.dietaryRestrictions ?? "",
      validator: z.string().nullish(),
    },
    medicalConditions: {
      value: model?.medicalConditions ?? "",
      validator: z.string().nullish(),
    },
    allergies: {
      value: model?.allergies ?? "",
      validator: z.string().nullish(),
    },
    name: {
      value: model?.name ?? "",
      validator: z.string(),
    },
    phone: {
      value: model?.phone ?? "",
      validator: z.string().nullish(),
    },
    birthdate: {
      value: model?.birthdate ?? "",
      validator: z.string().nullish(),
    },
    id: {
      value: model?.id ?? "",
      validator: z.string(),
    },
  });
  return (
    <DomainForm
      form={form}
      onUpsert={onUpsert}
      buttonTranslation={
        !model
          ? "reservations.form.clients.createButton"
          : "reservations.form.clients.updateButton"
      }
      fields={[
        {
          key: "name",
          label: "reservations.form.clients.fields.name",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.name}
              onChange={(e) => form.setValue("name", e.target.value)}
            />
          ),
        },
        {
          key: "email",
          label: "reservations.form.clients.fields.email",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.email}
              onChange={(e) => form.setValue("email", e.target.value)}
            />
          ),
        },
        {
          key: "phone",
          label: "reservations.form.clients.fields.phone",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.phone ?? ""}
              onChange={(e) => form.setValue("phone", e.target.value)}
            />
          ),
        },
        {
          key: "birthdate",
          label: "reservations.form.clients.fields.birthdate",
          input: (form, id) => (
            <Input
              id={id}
              type="date"
              value={form.value.birthdate ?? ""}
              onChange={(e) => form.setValue("birthdate", e.target.value)}
            />
          ),
        },
        {
          key: "height",
          label: "reservations.form.clients.fields.height",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              value={form.value.height ?? ""}
              onChange={(e) => form.setValue("height", Number(e.target.value))}
            />
          ),
        },
        {
          key: "inseam",
          label: "reservations.form.clients.fields.inseam",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              value={form.value.inseam ?? ""}
              onChange={(e) => form.setValue("inseam", Number(e.target.value))}
            />
          ),
        },
        {
          key: "emergencyContact",
          label: "reservations.form.clients.fields.emergencyContact",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.emergencyContact ?? ""}
              onChange={(e) =>
                form.setValue("emergencyContact", e.target.value)
              }
            />
          ),
        },
        {
          key: "ensured",
          label: "reservations.form.clients.fields.ensured",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              checked={!!form.value.ensured}
              onCheckedChange={(v) => form.setValue("ensured", v)}
            />
          ),
        },
        {
          key: "allergies",
          label: "reservations.form.clients.fields.allergies",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.allergies ?? ""}
              onChange={(e) => form.setValue("allergies", e.target.value)}
            />
          ),
        },
        {
          key: "dietaryRestrictions",
          label: "reservations.form.clients.fields.dietaryRestrictions",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.dietaryRestrictions ?? ""}
              onChange={(e) =>
                form.setValue("dietaryRestrictions", e.target.value)
              }
            />
          ),
        },
        {
          key: "medicalConditions",
          label: "reservations.form.clients.fields.medicalConditions",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.medicalConditions ?? ""}
              onChange={(e) =>
                form.setValue("medicalConditions", e.target.value)
              }
            />
          ),
        },
        {
          key: "passportExpiration",
          label: "reservations.form.clients.fields.passportExpiration",
          input: (form, id) => (
            <Input
              id={id}
              type="date"
              value={form.value.passportExpiration ?? ""}
              onChange={(e) =>
                form.setValue("passportExpiration", e.target.value)
              }
            />
          ),
        },
        {
          key: "passportNumber",
          label: "reservations.form.clients.fields.passportNumber",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.passportNumber ?? ""}
              onChange={(e) => form.setValue("passportNumber", e.target.value)}
            />
          ),
        },
      ]}
    ></DomainForm>
  );
}
